import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { 
  Settings, 
  Percent,
  Loader2,
  Save,
  Info
} from 'lucide-react';
import { api } from '../../api/client';
import { toast } from 'sonner';

const SettingsTab = ({ tenantId, onUpdate }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    default_cogs_percent: 48,
    currency: 'USD',
    fiscal_year_start: '01'
  });

  // Fetch settings
  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/v1/profitability/settings', {
        headers: { 'X-Tenant-ID': tenantId }
      });
      const data = response.data || response;
      setSettings({
        default_cogs_percent: data.default_cogs_percent || 48,
        currency: data.currency || 'USD',
        fiscal_year_start: data.fiscal_year_start || '01'
      });
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  // Save settings
  const handleSave = async () => {
    try {
      setSaving(true);
      await api.put('/api/v1/profitability/settings', settings, {
        headers: { 'X-Tenant-ID': tenantId }
      });
      
      toast.success('Settings saved');
      if (onUpdate) onUpdate();
      
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-emerald-400" />
      </div>
    );
  }

  return (
    <div className="space-y-3 max-w-xl">
      {/* Compact Header */}
      <div>
        <h3 className="text-base font-semibold text-white">Profitability Settings</h3>
        <p className="text-xs text-gray-400">Configure default values for calculations</p>
      </div>

      {/* Compact COGS Settings */}
      <div className="bg-[#1a2744] border border-gray-700/50 rounded-lg p-3">
        <div className="flex items-center gap-1.5 mb-2">
          <Percent className="w-3.5 h-3.5 text-emerald-400" />
          <h4 className="text-sm font-medium text-white">Cost of Goods Sold (COGS)</h4>
        </div>
        
        <div className="mb-2">
          <label className="block text-xs text-gray-400 mb-1">Default COGS %</label>
          <div className="flex items-center gap-2">
            <Input
              type="number"
              min="0"
              max="100"
              value={settings.default_cogs_percent}
              onChange={(e) => setSettings({ ...settings, default_cogs_percent: parseFloat(e.target.value) || 0 })}
              className="w-20 bg-[#0f1729] border-gray-600 text-white text-sm h-8 text-center"
            />
            <span className="text-gray-400 text-sm">%</span>
          </div>
          <p className="text-[10px] text-gray-500 mt-1">Used when actual COGS is unavailable. Typical: 30-60%</p>
        </div>

        {/* Compact Info Box */}
        <div className="bg-blue-500/10 border border-blue-500/20 rounded p-2 flex gap-2">
          <Info className="w-3.5 h-3.5 text-blue-400 flex-shrink-0 mt-0.5" />
          <div className="text-[10px] text-gray-400">
            <span className="text-blue-400 font-medium">How it works:</span> If order has actual COGS → uses actual. Otherwise → uses this default %
          </div>
        </div>
      </div>

      {/* Compact Display Settings */}
      <div className="bg-[#1a2744] border border-gray-700/50 rounded-lg p-3">
        <div className="flex items-center gap-1.5 mb-2">
          <Settings className="w-3.5 h-3.5 text-emerald-400" />
          <h4 className="text-sm font-medium text-white">Display Settings</h4>
        </div>
        
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs text-gray-400 mb-1">Currency</label>
            <select
              value={settings.currency}
              onChange={(e) => setSettings({ ...settings, currency: e.target.value })}
              className="w-full bg-[#0f1729] border border-gray-600 rounded px-2 py-1.5 text-white text-xs"
            >
              <option value="USD">USD ($)</option>
              <option value="EUR">EUR (€)</option>
              <option value="GBP">GBP (£)</option>
              <option value="INR">INR (₹)</option>
              <option value="CAD">CAD (C$)</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">Fiscal Year Start</label>
            <select
              value={settings.fiscal_year_start}
              onChange={(e) => setSettings({ ...settings, fiscal_year_start: e.target.value })}
              className="w-full bg-[#0f1729] border border-gray-600 rounded px-2 py-1.5 text-white text-xs"
            >
              <option value="01">January</option>
              <option value="04">April</option>
              <option value="07">July</option>
              <option value="10">October</option>
            </select>
          </div>
        </div>
      </div>

      {/* Compact COGS Reference */}
      <div className="bg-[#1a2744] border border-gray-700/50 rounded-lg p-3">
        <h4 className="text-xs font-medium text-white mb-2">Typical COGS by Industry</h4>
        <div className="grid grid-cols-3 gap-x-4 gap-y-1 text-[10px]">
          <div className="flex justify-between"><span className="text-gray-400">E-commerce</span><span className="text-white">40-60%</span></div>
          <div className="flex justify-between"><span className="text-gray-400">Amazon FBA</span><span className="text-white">30-50%</span></div>
          <div className="flex justify-between"><span className="text-gray-400">Private Label</span><span className="text-white">25-45%</span></div>
          <div className="flex justify-between"><span className="text-gray-400">Dropshipping</span><span className="text-white">60-80%</span></div>
          <div className="flex justify-between"><span className="text-gray-400">Wholesale</span><span className="text-white">50-70%</span></div>
          <div className="flex justify-between"><span className="text-gray-400">Manufacturing</span><span className="text-white">35-55%</span></div>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-emerald-600 hover:bg-emerald-700 h-8 text-xs px-3"
        >
          {saving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Save className="w-3 h-3 mr-1" />}
          Save Settings
        </Button>
      </div>
    </div>
  );
};

export default SettingsTab;
